import { inject, Injectable, signal } from '@angular/core';
import { LoggerService } from './logger.service';
import { UserStorageService } from './user-storage.service';

/**
 * Service for managing Eruda mobile debugger
 * Provides on-device developer tools for mobile debugging
 *
 * Eruda is only initialized if the global setting 'eruda_enabled' is turned on.
 * The eruda-init.js script checks localStorage directly before app startup.
 */
@Injectable({
  providedIn: 'root',
})
export class ErudaService {
  private erudaInstance: any = null;
  private loggerService = inject(LoggerService);
  private userStorage = inject(UserStorageService);
  private logger: any = null; // Lazy init after Eruda is ready

  /** Whether Eruda is currently visible */
  readonly isVisible = signal(false);

  /** Whether Eruda has been initialized */
  readonly isInitialized = signal(false);

  /** Whether Eruda is enabled (persisted to global localStorage) */
  readonly isEnabled = signal(
    this.userStorage.getGlobalTypedItem<boolean>('eruda_enabled') ?? false,
  );

  constructor() {
    // Skip Eruda in test environment (jsdom)
    if (typeof window.navigator !== 'undefined' && window.navigator.userAgent?.includes('jsdom')) {
      this.getLogger().debug('Skipping initialization in test environment');
      return;
    }

    // Check if Eruda was pre-initialized from index.html (only if setting was enabled)
    if ((window as any).eruda) {
      this.erudaInstance = (window as any).eruda;
      this.isInitialized.set(true);
      this.getLogger().debug('Using pre-initialized instance from index.html');

      // Show the entry button (panel is hidden by default)
      this.erudaInstance.get?.('entryBtn')?.show();
      // Ensure panel is hidden initially
      this.erudaInstance.hide();
      this.isVisible.set(false);
    } else {
      // Eruda not pre-initialized - will be initialized when user enables it
      this.getLogger().debug('Not initialized - disabled in settings');
    }
  }

  /**
   * Get logger lazily - only create after Eruda is initialized
   */
  private getLogger() {
    if (!this.logger) {
      this.logger = this.loggerService.getLogger('eruda');
    }
    return this.logger;
  }

  /**
   * Initialize Eruda (called when user enables it via settings)
   * This method can be called multiple times safely
   */
  async initialize(): Promise<void> {
    if (this.isInitialized()) {
      this.getLogger().debug('Eruda already initialized');
      return;
    }

    try {
      this.getLogger().debug('Initializing Eruda...');

      // Check if Eruda was already loaded globally (from eruda-init.js)
      if ((window as any).eruda) {
        this.getLogger().debug('Using globally loaded Eruda instance');
        this.erudaInstance = (window as any).eruda;
        this.isInitialized.set(true);
        this.getLogger().info('Eruda initialized successfully (from global)');
        return;
      }

      // Dynamically import Eruda if not already loaded
      const eruda = await import('eruda');

      // Initialize Eruda
      eruda.default.init();
      this.erudaInstance = eruda.default;
      this.isInitialized.set(true);

      this.getLogger().info('Eruda initialized successfully (dynamic import)');
    } catch (error) {
      this.getLogger().error('Failed to initialize Eruda:', error);
      throw error;
    }
  }

  /**
   * Show Eruda debugger panel (opens the full window.console)
   * Note: This is separate from showing the floating button
   */
  async show(): Promise<void> {
    if (!this.isInitialized()) {
      await this.initialize();
    }

    this.erudaInstance?.show();
    this.isVisible.set(true);
    this.getLogger().debug('Eruda panel shown');
  }

  /**
   * Hide Eruda debugger panel (closes the window.console)
   * Note: This is separate from hiding the floating button
   */
  hide(): void {
    this.erudaInstance?.hide();
    this.isVisible.set(false);
    this.getLogger().debug('Eruda panel hidden');
  }

  /**
   * Toggle Eruda debugger panel visibility
   */
  async toggle(): Promise<void> {
    if (this.isVisible()) {
      this.hide();
    } else {
      await this.show();
    }
  }

  /**
   * Enable Eruda - shows the floating button without opening the panel
   * User can click the floating button to open/close the panel as needed
   */
  async enable(): Promise<void> {
    this.userStorage.setGlobalTypedItem('eruda_enabled', true);
    this.isEnabled.set(true);

    if (!this.isInitialized()) {
      await this.initialize();
    }

    // Show the floating entry button (not the panel)
    // this.erudaInstance?.get?.('entryBtn')?.show();

    this.getLogger().info('Eruda enabled');
  }

  /**
   * Disable Eruda - hides the floating button and persists setting
   * Does NOT destroy the instance so logs continue to be captured
   */
  disable(): void {
    this.userStorage.setGlobalTypedItem('eruda_enabled', false);
    this.isEnabled.set(false);

    // Hide both the panel and the floating entry button
    if (this.erudaInstance) {
      this.erudaInstance.disable();
      this.isVisible.set(false);
      // this.erudaInstance.get?.('entryBtn')?.hide(); // Hide the entry button
    }

    this.getLogger().info('Eruda disabled');
  }

  /**
   * Destroy Eruda instance (cleanup)
   */
  destroy(): void {
    if (this.erudaInstance) {
      this.erudaInstance.destroy();
      this.erudaInstance = null;
      this.isInitialized.set(false);
      this.isVisible.set(false);
      this.getLogger().debug('Eruda destroyed');
    }
  }

  /**
   * Export all window.console logs from LoggerService buffer
   * Returns formatted log text that can be copied or downloaded
   */
  async exportLogs(): Promise<string> {
    return this.loggerService.exportLogs();
  }

  /**
   * Copy all window.console logs to clipboard
   */
  async copyLogsToClipboard(): Promise<boolean> {
    try {
      const logs = await this.exportLogs();
      await window.navigator.clipboard.writeText(logs);
      this.getLogger().log('Logs copied to clipboard');
      return true;
    } catch (error) {
      this.getLogger().error('Failed to copy logs to clipboard:', error);
      return false;
    }
  }
}
